<script lang="ts" setup>
import vPreloadImg from '@/ts/directives/v-preload-img.ts';
import type { ColorPaletteKey } from '@/ts/types/color-palette-types.ts';
import { getThemeColorCssVar, setupLinearGradientCss } from '@/ts/utils/utils.ts';
type LobbySecitonProps = {
    topColor?: ColorPaletteKey,
    topColorStop?: string,
    bottomColor?: ColorPaletteKey,
    bottomColorStop?: string,
    opacity?: number,
    bottomColorOpacity?: number,
    coverBottom?: boolean,
    bgImg?: Record<string, unknown>,
    bgGradientColors?: string,
    noSkew?: boolean,
    noOverflow?: boolean,
};
const props = withDefaults(defineProps<LobbySecitonProps>(), {
    topColor: 'none',
    topColorStop: '0%',
    bottomColor: 'none',
    bottomColorStop: '100%',
    opacity: 1,
    bottomColorOpacity: 1,
    coverBottom: false,
});
const bottomColorOpacityPerc = (props.bottomColorOpacity * 100) + '%';
</script>

<template>
    <div 
        :style="{
            '--top-color': getThemeColorCssVar(topColor),
            '--bottom-color': getThemeColorCssVar(bottomColor),
            '--top-color-stop': topColorStop,
            '--bottom-color-stop': bottomColorStop,
            '--opacity': opacity,
            '--bottom-color-opactiy-perc': bottomColorOpacityPerc,
            '--background': setupLinearGradientCss(bgGradientColors),
        }"
        :class="{
            'cover-bottom': coverBottom,
        }"
    class="section" v-mobile>
        <div 
            :class="{
                'has-gradient-colors': Boolean(bgGradientColors),
                'no-skew': noSkew,
                'no-overflow': noOverflow,
            }" 
            class="background" 
            v-mobile
        >
            <template v-if="bgImg">
                <div class="inner-bg" v-preload-img="bgImg"></div>
            </template>
        </div>
        <div class="content" v-mobile>
            <slot></slot>
        </div>
    </div>
</template>

<style lang="scss" scoped>
    @use '@/scss/shared';
    .section{
        position: relative;
        width: 100%;
        min-height: 100px;
        padding: 70px 0;
        &[data-mobile]{
            padding: 50px 0;
        }
        & > .background{
            @mixin bg-props(){
                position: absolute;
                top:0;
                left:0;
                width:100%;
                height:calc(100% + 1px);
                pointer-events: none;
            }
            z-index: 2;
            @include bg-props();
            &:after{
                content: "";
                opacity: var(--opacity);
                z-index: 4;
                @include bg-props();
            }
            &.has-gradient-colors{
                &:after{
                    background: var(--background);
                }
            }
            &:not(.has-gradient-colors){
                &:after{
                    background: linear-gradient(var(--top-color) var(--top-color-stop), color-mix(in srgb, var(--bottom-color) var(--bottom-color-opactiy-perc), transparent) var(--bottom-color-stop));
                }
            }
            &:not(.no-skew){
                @include shared.skew-transition();
            }
            &.no-skew{
                &:before{
                    content: "";
                    position: absolute;
                    width: 100%;
                    height: 20px;
                    background-color: #000;
                    bottom:-20px;
                    left:0;
                    z-index:0;
                }
            }
            &.no-overflow{
                overflow: hidden;
            }
            & > .inner-bg{
                background-position: center;
                background-size: cover;
                background-repeat: no-repeat;
                z-index: 3;
                @include bg-props();
            }
        }
        & > .content{
            position: relative;
            z-index: 5;
        }
        &.cover-bottom{
            & > .background{
                height: calc(100% + 100px);
            }
        }
    }
</style>